<template>
  <div class="v2-ship-step">
    <h3 class="v2-ship-step__title">
      {{ title }}
    </h3>
    <p class="v2-ship-step__subtitle">
      {{ subtitlePrefix }}
      <strong>"{{ collectionName }}"</strong>
      {{ subtitleSuffix }}
    </p>

    <div
      v-if="isLoading"
      class="v2-ship-step__loader"
    >
      <v2-spinner size="md" />
    </div>

    <template v-else>
      <div
        v-if="!products.length"
        class="v2-ship-step__empty"
      >
        No eligible products found for this collection.
      </div>

      <template v-else>
        <v2-input
          :value="searchQuery"
          placeholder="Search by product name or style codes (comma-separated)..."
          @input="searchQuery = $event"
        />

        <div class="v2-ship-step__toolbar">
          <v2-checkbox
            :value="allFilteredSelected"
            :label="`Select All (${filteredProducts.length})`"
            @input="toggleAll"
          />
          <span class="v2-ship-step__count">
            {{ localSelectedIds.length }} selected
          </span>
        </div>

        <div class="v2-ship-step__list">
          <div
            v-for="product in filteredProducts"
            :key="product.productId"
            class="v2-ship-product"
            :class="{ 'v2-ship-product--selected': localSelectedIds.includes(product.productId) }"
            @click="toggleProduct(product.productId)"
          >
            <!--
              click.stop wrapper — without it the native click bubbles
              up to the parent <div>'s @click which also calls
              toggleProduct, the two invocations cancel out, and the
              selection never changes. V1 used `@click.native.stop` on
              the b-form-checkbox for the same reason.
            -->
            <span @click.stop>
              <v2-checkbox
                :value="localSelectedIds.includes(product.productId)"
                @input="toggleProduct(product.productId)"
              />
            </span>
            <img
              class="v2-ship-product__img"
              :src="product.image || defaultImage"
              :alt="product.name"
              @error="onImageError"
            >
            <div class="v2-ship-product__meta">
              <div class="v2-ship-product__name">
                {{ product.name }}
              </div>
              <div
                v-if="product.styleCode"
                class="v2-ship-product__code"
              >
                {{ product.styleCode }}
              </div>
              <!--
                Two products can share a name and style code (different
                colourways), which leaves the rows indistinguishable. Where the
                row carries its shipments, naming them both tells the rows apart
                and is the information needed to choose between them.
              -->
              <div
                v-if="shipmentSummary(product)"
                class="v2-ship-product__shipments"
              >
                {{ shipmentSummary(product) }}
              </div>
            </div>
            <v2-badge :variant="getStatusVariant(product.status)">
              {{ getStatusText(product.status) }}
            </v2-badge>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import store from '@/store'
import { imageLoadError, getDefaultProductImage } from '@/@core/utils/utils'
import { getStatusTextAndColorCode } from '@/common-utils'
import { GET_BULK_SHIP_PRODUCTS } from '@/store/modules/order.module'
import V2Input from '@/components/v2/V2Input.vue'
import V2Checkbox from '@/components/v2/V2Checkbox.vue'
import V2Spinner from '@/components/v2/V2Spinner.vue'
import V2Badge from '@/components/v2/V2Badge.vue'

export default {
  name: 'V2ShipAllProductStep',
  components: {
    V2Input,
    V2Checkbox,
    V2Spinner,
    V2Badge,
  },
  props: {
    collectionId: { type: String, required: true },
    collectionName: { type: String, required: true },
    selectedProductIds: { type: Array, default: () => [] },
    // Optional override for where the products come from. Defaults to the
    // bulk-ship source; the unassign wizard passes a loader that returns the
    // products currently sitting on shipments instead.
    productsLoader: { type: Function, default: null },
    // Copy is overridable for the same reason. Defaults keep the Ship All
    // wording unchanged; the collection name sits between prefix and suffix.
    title: { type: String, default: 'Select Products to Ship' },
    subtitlePrefix: {
      type: String,
      default: 'Products across all confirmed orders in',
    },
    subtitleSuffix: { type: String, default: 'that are ready to ship.' },
  },
  data() {
    return {
      isLoading: false,
      products: [],
      localSelectedIds: [...this.selectedProductIds],
      defaultImage: getDefaultProductImage(),
      searchQuery: '',
    }
  },
  computed: {
    filteredProducts() {
      if (!this.searchQuery) return this.products
      const terms = this.searchQuery
        .split(',')
        .map(t => t.trim().toLowerCase())
        .filter(Boolean)
      if (!terms.length) return this.products
      return this.products.filter(p => terms.some(q => (p.name || '').toLowerCase().includes(q)
          || (p.styleCode || '').toLowerCase().includes(q)))
    },
    allFilteredSelected() {
      return this.filteredProducts.length > 0
        && this.filteredProducts.every(p => this.localSelectedIds.includes(p.productId))
    },
  },
  watch: {
    collectionId: {
      immediate: true,
      handler() {
        this.loadProducts()
      },
    },
  },
  methods: {
    // Only the unassign flow supplies shipments on a product row; the bulk-ship
    // source does not, so this renders nothing there.
    shipmentSummary(product) {
      const seqIds = (product.shipments || [])
        .map(shipment => shipment.shipmentSeqId)
        .filter(Boolean)
      if (!seqIds.length) return ''
      if (seqIds.length <= 2) return `On ${seqIds.join(', ')}`
      return `On ${seqIds.slice(0, 2).join(', ')} +${seqIds.length - 2} more`
    },
    async loadProducts() {
      if (!this.collectionId) return
      this.isLoading = true
      try {
        if (this.productsLoader) {
          this.products = await this.productsLoader(this.collectionId)
        } else {
          const res = await store.dispatch(GET_BULK_SHIP_PRODUCTS, {
            collectionId: this.collectionId,
          })
          this.products = res?.data?.data?.products || []
        }
      } catch {
        this.products = []
      } finally {
        this.isLoading = false
      }
    },
    getStatusText(status) {
      return getStatusTextAndColorCode(status).text || status
    },
    // V2Badge uses semantic variants instead of inline color codes; map
    // status text → v2 variant so styling stays consistent with the rest
    // of the v2 surfaces (orders list, badges in other tables, etc.)
    getStatusVariant(status) {
      const text = (this.getStatusText(status) || '').toLowerCase()
      if (text.includes('cancel') || text.includes('reject')) return 'destructive'
      if (text.includes('modif')) return 'warning'
      if (text.includes('confirm') || text.includes('ready') || text.includes('ship')) return 'success'
      return 'default'
    },
    onImageError(e) {
      imageLoadError(e)
    },
    toggleProduct(productId) {
      const idx = this.localSelectedIds.indexOf(productId)
      if (idx >= 0) {
        this.localSelectedIds.splice(idx, 1)
      } else {
        this.localSelectedIds.push(productId)
      }
      this.$emit('update', [...this.localSelectedIds])
    },
    toggleAll(checked) {
      if (checked) {
        const ids = this.filteredProducts.map(p => p.productId)
        this.localSelectedIds = [...new Set([...this.localSelectedIds, ...ids])]
      } else {
        const ids = new Set(this.filteredProducts.map(p => p.productId))
        this.localSelectedIds = this.localSelectedIds.filter(id => !ids.has(id))
      }
      this.$emit('update', [...this.localSelectedIds])
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-ship-step {
  padding: var(--v2-space-2) var(--v2-space-1);

  &__title {
    font-family: var(--v2-font-display);
    font-size: 1rem;
    font-weight: 600;
    color: var(--v2-foreground);
    margin: 0 0 var(--v2-space-2) 0;
  }

  &__subtitle {
    font-size: 0.875rem;
    color: var(--v2-muted-foreground);
    margin: 0 0 var(--v2-space-4) 0;
  }

  &__loader {
    display: flex;
    justify-content: center;
    padding: var(--v2-space-8) 0;
  }

  &__empty {
    text-align: center;
    color: var(--v2-muted-foreground);
    font-size: 0.875rem;
    padding: var(--v2-space-8) 0;
  }

  &__toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: var(--v2-space-3) 0 var(--v2-space-2);
  }

  &__count {
    font-size: 0.75rem;
    color: var(--v2-muted-foreground);
    font-weight: 600;
  }

  &__list {
    max-height: 400px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: var(--v2-space-2);
    padding-right: var(--v2-space-1);
  }
}

.v2-ship-product {
  display: flex;
  align-items: center;
  gap: var(--v2-space-3);
  padding: var(--v2-space-2) var(--v2-space-3);
  background: var(--v2-glass-surface-2);
  border: 1px solid var(--v2-glass-stroke);
  border-radius: var(--v2-radius-md);
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: var(--v2-glass-stroke-strong);
  }

  &--selected {
    border-color: var(--v2-primary);
    background: oklch(0.62 0.21 295 / 0.06);
  }

  &__img {
    width: 48px;
    height: 48px;
    object-fit: cover;
    border-radius: var(--v2-radius-sm);
    background: var(--v2-muted);
    flex-shrink: 0;
  }

  &__meta {
    flex: 1;
    min-width: 0;
  }

  &__name {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--v2-foreground);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__code {
    font-size: 0.75rem;
    color: var(--v2-muted-foreground);
    margin-top: 2px;
  }

  &__shipments {
    font-size: 0.75rem;
    color: var(--v2-muted-foreground);
    margin-top: 2px;
    opacity: 0.85;
  }
}
</style>
